// src/pages/Collection-page/Collections.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { FaPlus, FaSearch, FaMoneyBillWave, FaCheckCircle, FaTimes, FaChevronDown } from 'react-icons/fa';

import BASE_URL from '../../api';

const API_BASE = `${BASE_URL}/api/`;

const Collections = () => {
  const token = localStorage.getItem('token');
  const [collections, setCollections] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  // Searchable Dropdown States
  const [invSearchText, setInvSearchText] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    invoiceId: '',
    invoiceNumber: '',
    paidAmount: '',
    paymentDate: new Date().toISOString().split('T')[0]
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // ────── FETCH DATA ──────
  const fetchData = async () => {
    setLoading(true);
    try {
      const [invRes, colRes] = await Promise.all([
        fetch(`${API_BASE}/getctrl/invoice`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/getctrl/colection`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const [invJson, colJson] = await Promise.all([invRes.json(), colRes.json()]);

      if (invJson.success) setInvoices(invJson.data);
      if (colJson.success) setCollections(colJson.data);
    } catch (err) {
      toast.error('Failed to sync data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* ------------------------------------------------------------------ */
  /* SEARCH & DROPDOWN LOGIC                                           */
  /* ------------------------------------------------------------------ */

  const filteredCollections = useMemo(() => {
    const s = searchTerm.toLowerCase().trim();
    if (!s) return collections;
    return collections.filter(col => {
      const client = col.invoiceId?.clientId || {};
      const clientName = client.companyName || client.name || '';
      return (
        (col.invoiceNumber || '').toLowerCase().includes(s) ||
        clientName.toLowerCase().includes(s) ||
        (col.status || '').toLowerCase().includes(s)
      );
    });
  }, [collections, searchTerm]);

  const filteredInvoices = useMemo(() => {
    const s = invSearchText.toLowerCase().trim();
    return invoices.filter(inv => {
      const client = inv.clientId || {};
      const clientMatch = (client.name || '').toLowerCase().includes(s) ||
        (client.companyName || '').toLowerCase().includes(s);
      const invMatch = (inv.invoiceNumber || '').toLowerCase().includes(s);
      return invMatch || clientMatch;
    });
  }, [invoices, invSearchText]);

  const paginated = filteredCollections.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredCollections.length / itemsPerPage);

  const formatDate = d => d ? new Date(d).toLocaleDateString('en-GB') : 'N/A';

  // ────── HANDLERS ──────

  // Logic to handle clicking the dropdown input
  const handleDropdownClick = () => {
    if (formData.invoiceId) {
      // Clear selection if user clicks again to see the full list
      setFormData({ ...formData, invoiceId: '', invoiceNumber: '', paidAmount: '' });
      setInvSearchText('');
    }
    setIsDropdownOpen(true);
  };

  const selectInvoice = (inv) => {
    const client = inv.clientId || {};
    const displayName = client.companyName || client.name || 'N/A';

    // Calculate Balance to auto-fill amount
    const totalPaid = collections
      .filter(c => c.invoiceId?._id === inv._id)
      .reduce((sum, c) => sum + (c.paidAmount || 0), 0);
    const balance = Math.max(inv.grandTotal - totalPaid, 0);

    setFormData({
      ...formData,
      invoiceId: inv._id,
      invoiceNumber: inv.invoiceNumber,
      paidAmount: balance.toFixed(2) // AUTO-SET VALUE
    });

    setInvSearchText(`${inv.invoiceNumber} - ${displayName}`);
    setIsDropdownOpen(false);
  };

  const handleAddCollection = async () => {
    if (!formData.invoiceId || !formData.paidAmount) return toast.error('Required fields missing');
    try {
      const res = await fetch(`${API_BASE}/getctrl/colection`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, paidAmount: Number(formData.paidAmount) })
      });
      const json = await res.json();
      if (json.success) {
        toast.success('Payment successfully recorded!');
        setShowAddForm(false);
        setFormData({ invoiceId: '', invoiceNumber: '', paidAmount: '', paymentDate: new Date().toISOString().split('T')[0] });
        setInvSearchText('');
        fetchData();
      }
    } catch (err) { toast.error('Network connection error'); }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl font-bold mb-6 text-gray-800 uppercase">Payment Collections</h2>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 border-b pb-6">
            <div className="relative w-full md:w-96">
              <FaSearch className="absolute left-3 top-3 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search by invoice or client name..."
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 outline-none transition"
              />
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-bold shadow-md flex items-center gap-2"
            >
              <FaPlus /> RECORD PAYMENT
            </button>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full table-auto border-collapse">
              <thead className="bg-blue-600 text-white text-sm">
                <tr>
                  <th className="p-3 text-left font-bold uppercase">S.No</th>
                  <th className="p-3 text-left font-bold uppercase">Invoice No</th>
                  <th className="p-3 text-left font-bold uppercase">Client</th>
                  <th className="p-3 text-right font-bold uppercase">Bill Total</th>
                  <th className="p-3 text-right font-bold uppercase">Paid</th>
                  <th className="p-3 text-right font-bold uppercase">Balance</th>
                  <th className="p-3 text-left font-bold uppercase">Date</th>
                  <th className="p-3 text-left font-bold uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 text-sm">
                {!loading && paginated.map((col, i) => {
                  const client = col.invoiceId?.clientId || {};
                  const clientName = client.companyName || client.name || 'N/A';
                  const grandTotal = col.invoiceId?.grandTotal || 0;
                  const sNo = searchTerm
                    ? collections.findIndex(orig => orig._id === col._id) + 1
                    : (currentPage - 1) * itemsPerPage + i + 1;

                  const totalPaidForInv = collections
                    .filter(c => c.invoiceId?._id === col.invoiceId?._id)
                    .reduce((sum, c) => sum + (c.paidAmount || 0), 0);
                  const outstanding = Math.max(grandTotal - totalPaidForInv, 0);

                  return (
                    <tr key={col._id} className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 text-gray-600 font-medium">{sNo}</td>
                      <td className="p-3 font-bold text-blue-700">{col.invoiceNumber}</td>
                      <td className="p-3 font-semibold text-gray-800 uppercase">{clientName}</td>
                      <td className="p-3 text-right">₹{grandTotal.toLocaleString()}</td>
                      <td className="p-3 text-right font-bold text-green-600">₹{col.paidAmount.toLocaleString()}</td>
                      <td className="p-3 text-right font-bold text-red-600">₹{outstanding.toLocaleString()}</td>
                      <td className="p-3 text-gray-600">{formatDate(col.paymentDate)}</td>
                      <td className="p-3">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold flex items-center gap-1 w-fit ${col.status === 'Collected' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                          {col.status === 'Collected' && <FaCheckCircle size={10} />}
                          {col.status?.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ────── SEARCHABLE MODAL FORM ────── */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 animate-in fade-in">
            <div className="bg-white rounded-2xl max-w-lg w-full p-8 shadow-2xl relative">
              <button onClick={() => setShowAddForm(false)} className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors">
                <FaTimes size={20} />
              </button>

              <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center gap-3 border-b pb-4 uppercase tracking-tight">
                <div className="p-2 bg-green-100 text-green-600 rounded-lg"><FaMoneyBillWave /></div>
                Record Payment
              </h2>

              <div className="space-y-6">
                <div className="relative" ref={dropdownRef}>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1 block">Search Invoice</label>
                  <div
                    className={`flex items-center justify-between p-3 border rounded-xl cursor-pointer transition-all bg-gray-50 ${isDropdownOpen ? 'ring-2 ring-blue-500 border-transparent' : 'hover:border-blue-400'}`}
                    onClick={handleDropdownClick}
                  >
                    <input
                      type="text"
                      placeholder="Type Invoice No or Client Name..."
                      className="bg-transparent outline-none w-full cursor-pointer text-sm font-bold text-gray-700 placeholder:font-normal"
                      value={invSearchText}
                      onChange={(e) => {
                        setInvSearchText(e.target.value);
                        setIsDropdownOpen(true);
                      }}
                    />
                    {formData.invoiceId ? (
                      <div onClick={(e) => { e.stopPropagation(); setFormData({ ...formData, invoiceId: '', invoiceNumber: '', paidAmount: '' }); setInvSearchText(''); }}>
                        <FaTimes className="text-red-400 hover:text-red-600" />
                      </div>
                    ) : (
                      <FaChevronDown className={`text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    )}
                  </div>

                  {isDropdownOpen && (
                    <div className="absolute w-full mt-2 bg-white border rounded-xl shadow-2xl z-[60] max-h-60 overflow-y-auto overflow-x-hidden border-blue-100">
                      {filteredInvoices.length > 0 ? filteredInvoices.map(inv => {
                        const client = inv.clientId || {};
                        const displayName = client.companyName || client.name || 'Unknown';
                        const totalPaid = collections
                          .filter(c => c.invoiceId?._id === inv._id)
                          .reduce((sum, c) => sum + (c.paidAmount || 0), 0);
                        const bal = Math.max(inv.grandTotal - totalPaid, 0);

                        return (
                          <div
                            key={inv._id}
                            className="p-3 hover:bg-blue-50 cursor-pointer border-b last:border-0 transition-colors group"
                            onClick={() => selectInvoice(inv)}
                          >
                            <div className="flex justify-between items-center">
                              <span className="font-bold text-blue-700 text-sm group-hover:text-blue-800">{inv.invoiceNumber}</span>
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${bal <= 0 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                                {bal <= 0 ? 'FULLY PAID' : `BAL: ₹${bal.toLocaleString()}`}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1 uppercase font-medium">{displayName}</div>
                          </div>
                        );
                      }) : (
                        <div className="p-4 text-center text-gray-400 text-sm italic">No matching invoices</div>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1 block">Paid Amount (₹)</label>
                    <input
                      type="number"
                      className="w-full p-3 border rounded-xl bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none text-sm font-bold text-green-700"
                      placeholder="0.00"
                      value={formData.paidAmount}
                      onChange={e => setFormData({ ...formData, paidAmount: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1 block">Payment Date</label>
                    <input
                      type="date"
                      value={formData.paymentDate}
                      className="w-full p-3 border rounded-xl bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none text-sm font-semibold text-gray-700"
                      onChange={e => setFormData({ ...formData, paymentDate: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button onClick={() => setShowAddForm(false)} className="px-6 py-2.5 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 font-bold tracking-tight transition">CANCEL</button>
                  <button onClick={handleAddCollection} className="px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-bold tracking-tight transition shadow-lg shadow-blue-100"> PAYMENT</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Collections;